import { useEffect, useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import {
    matchKpisQueryOptions,
    goalkeeperKpisQueryOptions,
    playerMatchKpisQueryOptions,
    goalkeeperMatchStatsQueryOptions,
    type MatchInfo,
    type PlayerMatchKpi,
    type GoalkeeperMatchKpi,
    type PlayerStats,
    type GoalkeeperStats,
} from "@/api/ranking";
import { useSelectedMatch } from "@/context/matchContext";
import { useSelectedTeam } from "@/context/teamContext";
import { useViewMode, useDashboardComparison } from "../page";
import { Loader } from "@/components/general/common/loader";
import ComparisonEngine from "@/components/general/Dashboard/Comparison/ComparisonEngine";
import { ComparisonMatchPerformanceChart } from "@/components/general/Dashboard/Comparison/ComparisonMatchPerformanceChart";

export const ComparisonPage = () => {
    const { matchIds, setTournamentName } = useSelectedMatch();
    const { teamId } = useSelectedTeam();
    const { viewMode } = useViewMode();
    const { setSelectedPlayers: setContextSelectedPlayers } = useDashboardComparison();
    const [selectedPlayers, setSelectedPlayers] = useState<(PlayerStats | GoalkeeperStats)[]>([]);

    const isGoalkeeperComparison = selectedPlayers.length > 0 && selectedPlayers[0].role === "GK";

    const { data: kpiData, isLoading } = useQuery(matchKpisQueryOptions(matchIds.length > 0 ? matchIds : [], teamId));
    const { data: goalkeeperKpiData } = useQuery(
        goalkeeperKpisQueryOptions(matchIds.length > 0 ? matchIds : [], teamId)
    );

    const allPlayersData = useMemo(() => {
        const players = viewMode === "average" ? kpiData?.average : kpiData?.total;
        const goalkeepers = viewMode === "average" ? goalkeeperKpiData?.average : goalkeeperKpiData?.total;
        return [...(players || []), ...(goalkeepers || [])] as (PlayerStats | GoalkeeperStats)[];
    }, [viewMode, kpiData, goalkeeperKpiData]);

    const enrichedSelectedPlayers = useMemo(() => {
        if (selectedPlayers.length === 0) return [];

        return selectedPlayers.map((selectedPlayer) => {
            const currentData = allPlayersData.find((p) => p.playerId === selectedPlayer.playerId);
            return currentData || selectedPlayer;
        });
    }, [selectedPlayers, allPlayersData]);

    const playerAQueryPlayer = useQuery({
        ...playerMatchKpisQueryOptions(selectedPlayers[0]?.playerId || 0, matchIds, teamId),
        enabled: !isGoalkeeperComparison && selectedPlayers.length >= 1 && (selectedPlayers[0]?.playerId || 0) > 0,
    });
    const playerBQueryPlayer = useQuery({
        ...playerMatchKpisQueryOptions(selectedPlayers[1]?.playerId || 0, matchIds, teamId),
        enabled: !isGoalkeeperComparison && selectedPlayers.length >= 2 && (selectedPlayers[1]?.playerId || 0) > 0,
    });
    const playerAQueryGoalkeeper = useQuery({
        ...goalkeeperMatchStatsQueryOptions(selectedPlayers[0]?.playerId || 0, matchIds, teamId),
        enabled: isGoalkeeperComparison && selectedPlayers.length >= 1 && (selectedPlayers[0]?.playerId || 0) > 0,
    });
    const playerBQueryGoalkeeper = useQuery({
        ...goalkeeperMatchStatsQueryOptions(selectedPlayers[1]?.playerId || 0, matchIds, teamId),
        enabled: isGoalkeeperComparison && selectedPlayers.length >= 2 && (selectedPlayers[1]?.playerId || 0) > 0,
    });

    const playerAQuery = isGoalkeeperComparison ? playerAQueryGoalkeeper : playerAQueryPlayer;
    const playerBQuery = isGoalkeeperComparison ? playerBQueryGoalkeeper : playerBQueryPlayer;

    const transformPlayerDataForChart = useMemo(() => {
        const transformData = (
            playerMatchKpis: PlayerMatchKpi[] | GoalkeeperMatchKpi[] | undefined,
            matchesInfo: MatchInfo[] | undefined
        ) => {
            if (!playerMatchKpis || playerMatchKpis.length === 0) return undefined;

            if (!matchesInfo) return undefined;

            const kpis = (playerMatchKpis as (PlayerMatchKpi | GoalkeeperMatchKpi)[])
                .map((playerKpi) => {
                    const matchInfo = matchesInfo.find((m) => m.matchId === playerKpi.matchId);
                    if (!matchInfo) return null;

                    if (isGoalkeeperComparison && playerKpi.role === "GK") {
                        const gkKpi = playerKpi as GoalkeeperMatchKpi;
                        return {
                            matchDate: matchInfo.date,
                            homeTeam: matchInfo.homeTeam,
                            awayTeam: matchInfo.awayTeam,
                            homeGoals: matchInfo.homeGoals,
                            awayGoals: matchInfo.awayGoals,
                            totalPoints: gkKpi.totalPoints,
                            gsaxData: { points: gkKpi.pointsGSAx },
                            highDangerData: { points: gkKpi.pointsHighDangerShots },
                            lowDangerData: { points: gkKpi.pointsLowDangerShots },
                            lateralPassData: { points: gkKpi.pointsLateralPassShots },
                            screenedData: { points: gkKpi.pointsScreenedShots },
                            role: "GK",
                        };
                    } else {
                        const pKpi = playerKpi as PlayerMatchKpi;
                        return {
                            matchDate: matchInfo.date,
                            homeTeam: matchInfo.homeTeam,
                            awayTeam: matchInfo.awayTeam,
                            homeGoals: matchInfo.homeGoals,
                            awayGoals: matchInfo.awayGoals,
                            totalPoints: pKpi.totalPoints,
                            offensiveData: { points: pKpi.pointsOffensive },
                            defensiveData: { points: pKpi.pointsDefensive },
                            teamPlayData: { points: pKpi.pointsTeamPlay },
                            effortData: { points: pKpi.pointsEffort },
                            bonusData: { points: pKpi.pointsBonus },
                            role: pKpi.role,
                        };
                    }
                })
                .filter((item): item is NonNullable<typeof item> => item !== null);

            return { kpis };
        };

        const matchesInfo = isGoalkeeperComparison ? goalkeeperKpiData?.matches : kpiData?.matches;

        return {
            playerA: transformData(playerAQuery.data, matchesInfo),
            playerB: transformData(playerBQuery.data, matchesInfo),
        };
    }, [playerAQuery.data, playerBQuery.data, kpiData?.matches, goalkeeperKpiData?.matches, isGoalkeeperComparison]);

    const computedStats = useMemo(() => {
        const computeFieldStats = (kpis: PlayerMatchKpi[], base: PlayerStats): PlayerStats => {
            const activeKpis = viewMode === "average" ? kpis.filter((k) => k.totalPoints !== 0) : kpis;
            const n = activeKpis.length;
            if (n === 0) return base;
            const sum = (vals: number[]) => vals.reduce((a, b) => a + b, 0);
            const def = sum(activeKpis.map((k) => k.pointsDefensive));
            const off = sum(activeKpis.map((k) => k.pointsOffensive));
            const tp = sum(activeKpis.map((k) => k.pointsTeamPlay));
            const eff = sum(activeKpis.map((k) => k.pointsEffort));
            const bonus = sum(activeKpis.map((k) => k.pointsBonus));
            const total = sum(activeKpis.map((k) => k.totalPoints));
            if (viewMode === "average") {
                return {
                    ...base,
                    pointsDefensive: def / n,
                    pointsOffensive: off / n,
                    pointsTeamPlay: tp / n,
                    pointsEffort: eff / n,
                    pointsBonus: bonus / n,
                    totalPoints: total / n,
                    matchesPlayed: n,
                };
            }
            return {
                ...base,
                pointsDefensive: def,
                pointsOffensive: off,
                pointsTeamPlay: tp,
                pointsEffort: eff,
                pointsBonus: bonus,
                totalPoints: total,
                matchesPlayed: n,
            };
        };

        const computeGkStats = (kpis: GoalkeeperMatchKpi[], base: GoalkeeperStats): GoalkeeperStats => {
            const activeKpis = viewMode === "average" ? kpis.filter((k) => k.totalPoints !== 0) : kpis;
            const n = activeKpis.length;
            if (n === 0) return base;
            const sum = (vals: number[]) => vals.reduce((a, b) => a + b, 0);
            const gsax = sum(activeKpis.map((k) => k.pointsGSAx));
            const hd = sum(activeKpis.map((k) => k.pointsHighDangerShots));
            const ld = sum(activeKpis.map((k) => k.pointsLowDangerShots));
            const lp = sum(activeKpis.map((k) => k.pointsLateralPassShots));
            const sc = sum(activeKpis.map((k) => k.pointsScreenedShots));
            const total = sum(activeKpis.map((k) => k.totalPoints));
            if (viewMode === "average") {
                return {
                    ...base,
                    pointsGSAx: gsax / n,
                    pointsHighDangerShots: hd / n,
                    pointsLowDangerShots: ld / n,
                    pointsLateralPassShots: lp / n,
                    pointsScreenedShots: sc / n,
                    totalPoints: total / n,
                    matchesPlayed: n,
                };
            }
            return {
                ...base,
                pointsGSAx: gsax,
                pointsHighDangerShots: hd,
                pointsLowDangerShots: ld,
                pointsLateralPassShots: lp,
                pointsScreenedShots: sc,
                totalPoints: total,
                matchesPlayed: n,
            };
        };

        const result: (PlayerStats | GoalkeeperStats)[] = [...enrichedSelectedPlayers];

        if (!isGoalkeeperComparison) {
            if (enrichedSelectedPlayers[0] && playerAQueryPlayer.data && playerAQueryPlayer.data.length > 0) {
                result[0] = computeFieldStats(playerAQueryPlayer.data, enrichedSelectedPlayers[0] as PlayerStats);
            }
            if (enrichedSelectedPlayers[1] && playerBQueryPlayer.data && playerBQueryPlayer.data.length > 0) {
                result[1] = computeFieldStats(playerBQueryPlayer.data, enrichedSelectedPlayers[1] as PlayerStats);
            }
        } else {
            if (enrichedSelectedPlayers[0] && playerAQueryGoalkeeper.data && playerAQueryGoalkeeper.data.length > 0) {
                result[0] = computeGkStats(playerAQueryGoalkeeper.data, enrichedSelectedPlayers[0] as GoalkeeperStats);
            }
            if (enrichedSelectedPlayers[1] && playerBQueryGoalkeeper.data && playerBQueryGoalkeeper.data.length > 0) {
                result[1] = computeGkStats(playerBQueryGoalkeeper.data, enrichedSelectedPlayers[1] as GoalkeeperStats);
            }
        }

        return result;
    }, [
        enrichedSelectedPlayers,
        playerAQueryPlayer.data,
        playerBQueryPlayer.data,
        playerAQueryGoalkeeper.data,
        playerBQueryGoalkeeper.data,
        isGoalkeeperComparison,
        viewMode,
    ]);

    const [playerAName, playerBName] = useMemo(
        () => [
            computedStats[0] ? `${computedStats[0].firstName} ${computedStats[0].lastName}` : "",
            computedStats[1] ? `${computedStats[1].firstName} ${computedStats[1].lastName}` : "",
        ],
        [computedStats]
    );

    const isPlayerDataLoading = playerAQuery.isLoading || playerBQuery.isLoading;

    useEffect(() => {
        if (kpiData?.matches && kpiData.matches.length > 0) {
            setTournamentName(kpiData.matches[0].tournamentName);
        }
    }, [kpiData, setTournamentName]);

    useEffect(() => {
        const contextPlayers = computedStats.map((player) => ({
            playerId: player.playerId,
            firstName: player.firstName,
            lastName: player.lastName,
        }));
        setContextSelectedPlayers(contextPlayers);
    }, [computedStats, setContextSelectedPlayers]);

    if (isLoading) {
        return (
            <div className="flex flex-col gap-4 min-h-0">
                <div className="glass card rounded-xl p-6 flex items-center justify-center h-48 sm:h-64">
                    <Loader message="Loading comparison engine..." />
                </div>
                <div className="glass card rounded-xl p-6 flex items-center justify-center h-[300px] sm:h-[518px]">
                    <Loader message="Loading match performance..." />
                </div>
            </div>
        );
    }

    return (
        <section className="flex flex-col gap-4 overflow-auto">
            <div
                id="onboarding-compare-engine"
                className="flex flex-col gap-4 p-3 border border-table-border rounded-md bg-comparison-bg"
            >
                {allPlayersData && allPlayersData.length === 0 ? (
                    <div className="flex items-center justify-center py-8">
                        <span className="text-center p-4 text-on-surface-variant">
                            No players available for comparison
                        </span>
                    </div>
                ) : (
                    <ComparisonEngine
                        data={computedStats}
                        availablePlayersForSelection={allPlayersData}
                        isLoading={isLoading}
                        matchIds={matchIds}
                        selectedPlayers={computedStats}
                        viewMode={viewMode}
                        setSelectedPlayers={setSelectedPlayers}
                    />
                )}
            </div>
            <div id="onboarding-compare-performance">
                <ComparisonMatchPerformanceChart
                    playerAData={transformPlayerDataForChart.playerA}
                    playerBData={transformPlayerDataForChart.playerB}
                    playerAName={playerAName}
                    playerBName={playerBName}
                    isLoading={isPlayerDataLoading}
                />
            </div>
        </section>
    );
};
